import { useEffect, useRef, useMemo, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, Navigation, Phone } from 'lucide-react';

// ── Decode Google encoded polyline ─────────────────────────
function decodePolyline(encoded) {
  if (!encoded) return [];
  let index = 0, lat = 0, lng = 0;
  const coordinates = [];
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : result >> 1;
    coordinates.push([lat / 1e5, lng / 1e5]);
  }
  return coordinates;
}

// ── Navigation action sheet ────────────────────────────────
function NavSheet({ lat, lng, label, onClose }) {
  function openGoogleMaps() {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank');
    onClose();
  }
  function openWaze() {
    window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank');
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl p-4 space-y-2 pb-8">
        <div className="flex justify-center mb-2">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>
        <p className="text-xs text-center text-gray-400 font-medium mb-1">
          Navegar a: <span className="text-gray-700 font-semibold">{label}</span>
        </p>
        <button
          onClick={openGoogleMaps}
          className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all rounded-2xl px-4 py-4"
        >
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center shrink-0 text-lg">
            🗺️
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-gray-800">Google Maps</div>
            <div className="text-xs text-gray-400">Abrir en Google Maps</div>
          </div>
        </button>
        <button
          onClick={openWaze}
          className="w-full flex items-center gap-3 bg-gray-50 hover:bg-gray-100 active:scale-95 transition-all rounded-2xl px-4 py-4"
        >
          <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0 text-lg">
            🚗
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-gray-800">Waze</div>
            <div className="text-xs text-gray-400">Abrir en Waze</div>
          </div>
        </button>
        <button onClick={onClose} className="w-full py-3.5 rounded-2xl text-sm font-semibold text-gray-500 bg-gray-100 mt-1">
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default function DriverExpressMap({ delivery, onClose, focusOnPickup = false }) {
  const mapContainerRef = useRef(null);
  const mapRef          = useRef(null);
  const driverMarkerRef = useRef(null);
  const watchIdRef      = useRef(null);

  // Navigation sheet state
  const [navTarget, setNavTarget] = useState(null); // { lat, lng, label }

  const polylineCoords = useMemo(
    () => decodePolyline(delivery.route_polyline),
    [delivery.route_polyline]
  );

  // Map center — focus on pickup if en_recojo, otherwise midpoint
  const center = useMemo(() => {
    if (focusOnPickup && delivery.pickup_lat) {
      return [delivery.pickup_lat, delivery.pickup_lng];
    }
    if (delivery.pickup_lat && delivery.dropoff_lat) {
      return [
        (delivery.pickup_lat + delivery.dropoff_lat) / 2,
        (delivery.pickup_lng + delivery.dropoff_lng) / 2,
      ];
    }
    return [-12.046374, -77.042793];
  }, [delivery, focusOnPickup]);

  // ── Driver position icon ───────────────────────────────────
  function createDriverPositionIcon() {
    return L.divIcon({
      className: '',
      html: `<div style="
        width:20px;height:20px;border-radius:50%;
        background:#B1336E;border:3px solid white;
        box-shadow:0 2px 8px rgba(177,51,110,0.6);
      "></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
  }

  // ── Initialize map imperatively ────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    const map = L.map(mapContainerRef.current, { center, zoom: focusOnPickup ? 15 : 13, zoomControl: true });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // Pickup marker (green)
    if (delivery.pickup_lat) {
      const pickupIcon = L.divIcon({
        className: '',
        html: `<div style="width:32px;height:32px;border-radius:50% 50% 50% 0;background:#67A43C;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);transform:rotate(-45deg);"></div>`,
        iconSize: [32, 32], iconAnchor: [16, 32],
      });
      L.marker([delivery.pickup_lat, delivery.pickup_lng], { icon: pickupIcon })
        .addTo(map)
        .bindPopup(`<div style="font-size:12px"><strong style="color:#67A43C">📦 Recojo</strong><br/>${delivery.pickup_address}</div>`);
    }

    // Dropoff marker (magenta)
    if (delivery.dropoff_lat) {
      const dropoffIcon = L.divIcon({
        className: '',
        html: `<div style="width:32px;height:32px;border-radius:50% 50% 50% 0;background:#B1336E;border:3px solid white;box-shadow:0 2px 8px rgba(177,51,110,0.4);transform:rotate(-45deg);"></div>`,
        iconSize: [32, 32], iconAnchor: [16, 32],
      });
      L.marker([delivery.dropoff_lat, delivery.dropoff_lng], { icon: dropoffIcon })
        .addTo(map)
        .bindPopup(`<div style="font-size:12px"><strong style="color:#B1336E">🏁 Entrega</strong><br/>${delivery.dropoff_address}<br/>${delivery.recipient_name}</div>`);
    }

    // Route polyline
    if (polylineCoords.length > 0) {
      L.polyline(polylineCoords, { color: '#B1336E', weight: 5, opacity: 0.85 }).addTo(map);
      map.fitBounds(L.polyline(polylineCoords).getBounds(), { padding: [40, 40] });
    } else if (delivery.pickup_lat && delivery.dropoff_lat) {
      map.fitBounds([
        [delivery.pickup_lat, delivery.pickup_lng],
        [delivery.dropoff_lat, delivery.dropoff_lng],
      ], { padding: [60, 60] });
    }

    // ── Live driver position via watchPosition ─────────────
    if (navigator.geolocation) {
      // Show initial position immediately
      navigator.geolocation.getCurrentPosition(pos => {
        const { latitude, longitude } = pos.coords;
        if (!mapRef.current) return;
        if (driverMarkerRef.current) {
          driverMarkerRef.current.setLatLng([latitude, longitude]);
        } else {
          driverMarkerRef.current = L.marker([latitude, longitude], { icon: createDriverPositionIcon() })
            .addTo(mapRef.current)
            .bindPopup('<div style="font-size:12px">📍 Tu ubicación</div>');
        }
      }, null, { enableHighAccuracy: true });

      // Then watch for updates
      watchIdRef.current = navigator.geolocation.watchPosition(
        pos => {
          const { latitude, longitude } = pos.coords;
          if (!mapRef.current) return;
          if (driverMarkerRef.current) {
            driverMarkerRef.current.setLatLng([latitude, longitude]);
          } else {
            driverMarkerRef.current = L.marker([latitude, longitude], { icon: createDriverPositionIcon() })
              .addTo(mapRef.current)
              .bindPopup('<div style="font-size:12px">📍 Tu ubicación</div>');
          }
        },
        err => console.warn('[DriverMap] watchPosition error:', err),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    }

    return () => {
      // Stop watching position when map unmounts
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {navTarget && (
        <NavSheet
          lat={navTarget.lat}
          lng={navTarget.lng}
          label={navTarget.label}
          onClose={() => setNavTarget(null)}
        />
      )}

      <div className="fixed inset-0 bg-black z-50 flex flex-col font-montserrat">

        {/* Top bar */}
        <div className="bg-white px-4 py-3 flex items-center gap-3 shrink-0">
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-xl transition-colors">
            <X size={20} className="text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">
              {delivery.recipient_name}
            </div>
            <div className="text-xs text-gray-400 truncate">
              {focusOnPickup ? delivery.pickup_address : delivery.dropoff_address}
            </div>
          </div>
          <div className="text-sm font-bold text-brand-green shrink-0">
            S/ {Number(delivery.driver_earnings).toFixed(2)}
          </div>
        </div>

        {/* Map */}
        <div ref={mapContainerRef} style={{ flex: 1, minHeight: 0 }} />

        {/* Bottom strip */}
        <div className="bg-white px-4 py-4 space-y-3 shrink-0">

          {/* Address cards — tappable to open nav sheet */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <button
              onClick={() => setNavTarget({ lat: delivery.pickup_lat, lng: delivery.pickup_lng, label: 'Punto de recojo' })}
              className="bg-brand-green/10 rounded-xl px-3 py-2 text-left active:scale-95 transition-transform"
            >
              <div className="text-brand-green font-semibold mb-0.5 flex items-center gap-1">
                <Navigation size={10} />📦 Recojo
              </div>
              <div className="text-gray-600 leading-snug line-clamp-2">{delivery.pickup_address}</div>
            </button>
            <button
              onClick={() => setNavTarget({ lat: delivery.dropoff_lat, lng: delivery.dropoff_lng, label: 'Punto de entrega' })}
              className="bg-brand-primary/10 rounded-xl px-3 py-2 text-left active:scale-95 transition-transform"
            >
              <div className="text-brand-primary font-semibold mb-0.5 flex items-center gap-1">
                <Navigation size={10} />🏁 Entrega
              </div>
              <div className="text-gray-600 leading-snug line-clamp-2">{delivery.dropoff_address}</div>
            </button>
          </div>

          {/* Call button */}
          <a
            href={`tel:${delivery.recipient_phone}`}
            className="flex items-center justify-center gap-2 bg-brand-green text-white py-3.5 rounded-2xl text-sm font-bold active:scale-95 transition-transform"
          >
            <Phone size={16} />
            Llamar al destinatario
          </a>
        </div>
      </div>
    </>
  );
}